import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Layout from '../components/Layout';
import AddPortfolioPropertyModal from '../components/AddPortfolioPropertyModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import DigestSettingsModal from '../components/DigestSettingsModal';
import { formatCurrency } from '../utils/formatters';
import { STATUS_CONFIG } from '../config/statusConfig';
import { API_URL } from '../config/api';
import { Briefcase, Bell, TrendingUp, DollarSign, Plus, Trash2, Mail } from 'lucide-react';
import './PortfolioPage.css';

const PortfolioPage = () => {
    const { user, session } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDigestOpen, setIsDigestOpen] = useState(false);

    const fetchPortfolio = useCallback(async () => {
        if (!session?.access_token) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/portfolio`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (res.ok) {
                setData(await res.json());
            } else {
                showToast('Failed to load portfolio', 'error');
            }
        } catch {
            showToast('Network error loading portfolio', 'error');
        } finally {
            setLoading(false);
        }
    }, [session, showToast]);

    useEffect(() => {
        if (user) fetchPortfolio();
    }, [user, fetchPortfolio]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            const res = await fetch(`${API_URL}/portfolio/${deleteTarget.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (res.ok) {
                showToast('Removed from portfolio', 'success');
                setDeleteTarget(null);
                fetchPortfolio();
            } else {
                showToast('Failed to remove', 'error');
            }
        } catch {
            showToast('Network error', 'error');
        }
    };

    const stats = data?.stats || {};
    const items = data?.items || [];

    return (
        <Layout scrollable={true}>
            <div className="portfolio-page">
                <div className="portfolio-container">
                    {/* Header */}
                    <div className="portfolio-header">
                        <div>
                            <h1 className="portfolio-title">My Portfolio</h1>
                            <p className="portfolio-subtitle">
                                Track your rental portfolio with valuation alerts and market updates.
                            </p>
                        </div>
                        <div className="portfolio-header-actions">
                            {items.length > 0 && (
                                <button
                                    className="btn-secondary-portfolio"
                                    onClick={() => setIsDigestOpen(true)}
                                >
                                    <Mail size={16} />
                                    Digest Settings
                                </button>
                            )}
                            <button className="btn-primary-portfolio" onClick={() => setIsAddModalOpen(true)}>
                                <Plus size={16} />
                                Add Property
                            </button>
                        </div>
                    </div>

                    {/* Stats tiles */}
                    {!loading && items.length > 0 && (
                        <div className="portfolio-stats-row">
                            <div className="portfolio-stat-tile">
                                <div className="stat-icon"><Briefcase size={20} /></div>
                                <div className="stat-value">{stats.property_count ?? 0}</div>
                                <div className="stat-label">Properties</div>
                            </div>
                            <div className="portfolio-stat-tile">
                                <div className="stat-icon"><TrendingUp size={20} /></div>
                                <div className="stat-value">{formatCurrency(stats.total_equity)}</div>
                                <div className="stat-label">Total Equity</div>
                            </div>
                            <div className="portfolio-stat-tile">
                                <div className="stat-icon"><DollarSign size={20} /></div>
                                <div className="stat-value">{formatCurrency(stats.potential_revenue)}</div>
                                <div className="stat-label">Potential Revenue</div>
                            </div>
                            <div className="portfolio-stat-tile">
                                <div className="stat-icon"><Bell size={20} /></div>
                                <div className="stat-value">{stats.active_alerts ?? 0}</div>
                                <div className="stat-label">Active Alerts</div>
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    {loading ? (
                        <div className="portfolio-loading"><div className="spinner"></div></div>
                    ) : items.length === 0 ? (
                        <div className="portfolio-empty-state">
                            <Briefcase size={48} strokeWidth={1.5} />
                            <h2>No Properties Yet</h2>
                            <p>Add a property to start tracking ARV alerts and market updates.</p>
                            <button className="btn-primary-portfolio" onClick={() => setIsAddModalOpen(true)}>
                                <Plus size={16} /> Add Your First Property
                            </button>
                        </div>
                    ) : (
                        <div className="portfolio-grid">
                            {items.map(item => (
                                <div
                                    key={item.id}
                                    className="portfolio-card"
                                    onClick={() => navigate(`/portfolio/${item.id}`)}
                                >
                                    <div className="pc-image-wrap">
                                        {item.primary_photo_url ? (
                                            <img src={item.primary_photo_url} alt={item.address} className="pc-image" />
                                        ) : (
                                            <div className="pc-image-placeholder">No Photo</div>
                                        )}
                                        {item.listing_status && item.listing_status !== 'UNKNOWN' && STATUS_CONFIG[item.listing_status] && (
                                            <span
                                                className="pc-status-chip"
                                                style={{ color: STATUS_CONFIG[item.listing_status].color }}
                                            >
                                                {STATUS_CONFIG[item.listing_status].label}
                                            </span>
                                        )}
                                        {item.active_alert_count > 0 && (
                                            <span className="pc-alert-chip">
                                                <Bell size={12} /> {item.active_alert_count}
                                            </span>
                                        )}
                                        <button
                                            className="pc-remove-btn"
                                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(item); }}
                                            aria-label="Remove from portfolio"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    <div className="pc-info">
                                        <div className="pc-price">{formatCurrency(item.list_price)}</div>
                                        <div className="pc-details">
                                            {item.beds || '-'} bd · {item.baths || '-'} ba · {item.sqft ? `${Number(item.sqft).toLocaleString()} sqft` : '-'}
                                        </div>
                                        <div className="pc-address">{item.address || 'Unknown'}</div>
                                        <div className="pc-metrics">
                                            {item.arv ? (
                                                <span>ARV {formatCurrency(item.arv)}</span>
                                            ) : (
                                                <span className="pc-pending">ARV pending</span>
                                            )}
                                            {item.estimated_rent && (
                                                <span className="pc-rent">${Math.round(item.estimated_rent).toLocaleString()}/mo</span>
                                            )}
                                        </div>
                                        {item.arv && item.list_price && (
                                            <div className="pc-equity">
                                                Equity: {formatCurrency(item.arv - item.list_price)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <AddPortfolioPropertyModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSuccess={() => { setIsAddModalOpen(false); fetchPortfolio(); }}
            />

            <DigestSettingsModal
                isOpen={isDigestOpen}
                onClose={() => setIsDigestOpen(false)}
            />

            {deleteTarget && (
                <DeleteConfirmationModal
                    isOpen={!!deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={handleDelete}
                    title="Remove from Portfolio"
                    message={`Remove "${deleteTarget.address || 'this property'}" from your portfolio? This will delete all alert configuration for this property.`}
                />
            )}
        </Layout>
    );
};

export default PortfolioPage;
